import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons";

const REFRESH_INTERVAL_MS = 30000;

function formatUptime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}h ${m}m ${s}s`;
}

function CheckRow({ label, value }) {
  const ok = value === "ok";
  return (
    <Group position="apart">
      <Text size="sm">{label}</Text>
      <Badge color={ok ? "green" : "red"} variant="light">
        {ok ? "OK" : value === "down" ? "Down" : value}
      </Badge>
    </Group>
  );
}

const Health = () => {
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchHealth = useCallback(async () => {
    setState("loading");
    setError(null);

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/health`);
      const json = await res.json();

      if (!res.ok || json?.data?.status !== "ok") {
        setState("unhealthy");
        setData(json?.data || null);
        setError(json?.message || `HTTP ${res.status}`);
      } else {
        setState("healthy");
        setData(json.data);
      }
    } catch (err) {
      setState("unhealthy");
      setData(null);
      setError(err.message || "Failed to reach server");
    }

    setLastChecked(new Date());
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const statusLabel =
    state === "loading"
      ? "Checking..."
      : state === "healthy"
        ? "Running"
        : data
          ? "Degraded"
          : "Unreachable";

  const statusColor =
    state === "healthy" ? "green" : state === "loading" ? "gray" : "red";

  return (
    <Container size={420} my={40}>
      <Title
        align="center"
        sx={(theme) => ({
          fontFamily: `Greycliff CF, ${theme.fontFamily}`,
          fontWeight: 900,
        })}
      >
        System Health
      </Title>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        <Stack spacing="md">
          <Group position="center">
            <Badge size="lg" color={statusColor} variant="filled">
              {statusLabel}
            </Badge>
          </Group>

          {data && (
            <>
              <Text align="center" size="sm" color="dimmed">
                {data.app} v{data.version}
              </Text>

              <CheckRow label="API" value={data.checks?.api || "unknown"} />
              <CheckRow
                label="Database"
                value={data.checks?.database || "unknown"}
              />

              <Group position="apart">
                <Text size="sm">Server uptime</Text>
                <Text size="sm" weight={500}>
                  {formatUptime(data.uptime || 0)}
                </Text>
              </Group>
            </>
          )}

          {error && state === "unhealthy" && (
            <Text size="sm" color="red">
              {error}
            </Text>
          )}

          {lastChecked && (
            <Text size="xs" color="dimmed" align="center">
              Last checked: {lastChecked.toLocaleString()}
            </Text>
          )}

          <Button
            fullWidth
            leftIcon={<IconRefresh size={16} />}
            onClick={fetchHealth}
            loading={state === "loading"}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default Health;
